import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import Holidays from "date-holidays";
import {
  DefaultModalProps,
  ModalSkeleton,
  SelectField,
  TextField,
} from "../../../components";
import { Country, BankHoliday } from "../../../API";
import { mapOptions } from "../../../lib";
import DateControl from "../../../components/DatePickers/DateControl";
import { countryOptions } from "../../../lib/utils";
import {
  checkIfExistingBankHoliday,
  FormFields,
  mapDefaultValues,
} from "./utils";

type Props = DefaultModalProps & {
  title: string;
  onSubmit: ({ data }: { data: FormFields }) => void;
  testID: string;
  params: {
    row: BankHoliday;
    bankHolidays?: BankHoliday[];
  };
};

export const RequestModal = ({
  title,
  onSubmit,
  testID,
  params: { row, bankHolidays },
  ...rest
}: Props) => {
  const hd = new Holidays();

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: mapDefaultValues(row),
  });
  const [countyOptions, setCountyOptions] = useState(
    mapOptions(hd.getStates(row?.countryCode ?? Country.GB))
  );

  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  const handleCountryChange = (value: Country) => {
    setValue("countryCode", value);
    setValue("counties", []);
    setCountyOptions(mapOptions(hd.getStates(value)));
  };

  return (
    <ModalSkeleton
      {...rest}
      title={title}
      isForm
      primaryAction={{
        label: "Save",
        onSubmit: handleSubmit((data) => {
          if (
            checkIfExistingBankHoliday({
              bankHolidays,
              counties: data.counties,
              country: data.countryCode,
              currentBankHolidayId: data.id,
              date: data.date,
            })
          ) {
            showSnackbar("Bank holiday already exist in the system", true);
            return;
          }

          return onSubmit({ data } as { data: FormFields });
        }),
        testID: "save-request",
      }}
      testID={testID}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", md: "repeat(2, 1fr)" },
          alignItems: "center",
          gap: 2,
        }}
      >
        <SelectField
          name="countryCode"
          control={control}
          label="Country"
          placeholder="Select country"
          options={countryOptions}
          onChange={(e) => handleCountryChange(e.target.value as Country)}
          required
        />

        <DateControl
          name="date"
          control={control}
          label="Holiday Date"
          onChange={(val) => setValue("date", val ?? "")}
        />

        <TextField name="localName" control={control} label="Holiday Name" />

        <SelectField
          name="counties"
          control={control}
          label="Counties"
          options={countyOptions}
          multiple
        />
      </Box>
    </ModalSkeleton>
  );
};
